/**
 * Procedural Heater Geometry
 *
 * Builds detailed 3D heater models differentiated by type:
 *   - Wood-fired (vedeldad):  Based on Harvia M3 / Narvi Luosto design
 *     Cast-iron body, glass fire door with frame, ash drawer, stone basket
 *     with stainless steel cage bars, adjustable legs, and a forward-thrown fire glow.
 *   - Electric (elaggregat):  Stainless steel tower + vertical fins + stone cage + control panel
 */
import * as THREE from "three";
import type { HeaterSizeMm } from "../domain/interior";
import { addBox } from "./geometry";
import { createHeaterStones } from "./heaterStones";
import {
	matChimney,
	matFireGlass,
	matHeater,
	matHeaterDoor,
	matHeaterElectric,
	matHeaterStone,
	matStainlessCage,
} from "./materials";
import {
	ELECTRIC_HEATER_ASSET_PATH,
	markPremiumSlot,
	WOOD_HEATER_ASSET_PATH,
} from "./premiumDetails";

// ─── Dimensions ─────────────────────────────────────────────

/**
 * Wood-fired heater: based on Harvia M3 (390×450×710mm)
 * Our model is slightly simplified but proportionally accurate.
 */
import {
	getHeaterDepthM,
	getHeaterHeightM,
	getHeaterWidthM,
	type HeaterRef,
	heaterKindOf,
} from "../domain/geometry/buildingDimensions";
import {
	type HeaterModel,
	resolveHeaterModel,
} from "../domain/heater/heaterCatalog";

// Owned by domain/geometry/buildingDimensions — placement, clearance and the 3D
// model must all measure the same box.
const WOOD_W = getHeaterWidthM("wood");
const WOOD_H = getHeaterHeightM("wood");
const WOOD_D = getHeaterDepthM("wood");
const WOOD_LEG_H = 0.07;

/** Electric heater: ~0.30 × 0.90 × 0.30 m (Tylö Sense / Harvia Cilindro tower style) */
const ELEC_W = getHeaterWidthM("electric");
const ELEC_H = getHeaterHeightM("electric");
const ELEC_D = getHeaterDepthM("electric");

// Fire-door glass lives in materials.ts now (matFireGlass) so it is
// lazy/disposable and pairs with matFireGlassBudget for tier-gated
// transmission — see applyGlassQualityMode.

function createSteamTexture(): THREE.DataTexture {
	const size = 64;
	const data = new Uint8Array(size * size * 4);
	for (let y = 0; y < size; y += 1) {
		for (let x = 0; x < size; x += 1) {
			const tx = x / Math.max(1, size - 1) - 0.5;
			const ty = y / Math.max(1, size - 1) - 0.5;
			const radius = Math.sqrt(tx * tx + ty * ty);
			const alpha = Math.max(0, 1 - (radius * 1.8) ** 2.2);
			const index = (y * size + x) * 4;
			data[index] = 255;
			data[index + 1] = 255;
			data[index + 2] = 255;
			data[index + 3] = Math.round(alpha * 255);
		}
	}
	const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
	texture.colorSpace = THREE.SRGBColorSpace;
	texture.needsUpdate = true;
	return texture;
}

const STEAM_TEXTURE = createSteamTexture();

// ─── Wood-fired heater ─────────────────────────────────────

/** Wood-heater parts that stay procedural when the modelled stove is shown:
 *  the stones, the animated fire-door glass, the lights and the steam. */
const WOOD_PROCEDURAL_PART =
	/-(stones|door-glass|fire-glow|fire-target|ember-glow|steam-)/;

function buildWoodHeater(
	position: THREE.Vector3,
	parent: THREE.Group,
	namePrefix: string,
	premiumAsset = false,
) {
	// Built into a shell the viewer can swap for the modelled stove
	// (scripts/blender/wood-heater.py); the parts the asset does not replace
	// move back out at the end. The shell has an identity transform, so
	// moving a part keeps it where it was built.
	const group = new THREE.Group();
	group.name = `detail/heater/${namePrefix}-shell`;
	parent.add(group);
	if (premiumAsset) {
		markPremiumSlot(group, {
			assetPath: WOOD_HEATER_ASSET_PATH,
			origin: [position.x, position.y - WOOD_H / 2, position.z],
		});
	}
	const bodyH = WOOD_H - WOOD_LEG_H;
	const combustionH = bodyH * 0.62; // lower 62% is the fire box
	const stoneH = bodyH * 0.38; // upper 38% is the stone basket
	const bodyBottomY = position.y - WOOD_H / 2 + WOOD_LEG_H;
	const combustionCenterY = bodyBottomY + combustionH / 2;
	const stoneCenterY = bodyBottomY + combustionH + stoneH / 2;

	// ── Main combustion body (lower section, dark cast iron) ──
	addBox(
		WOOD_W,
		combustionH,
		WOOD_D,
		matHeater,
		position.x,
		combustionCenterY,
		position.z,
		group,
	).name = `detail/heater/${namePrefix}-body`;

	// ── Glass fire door with cast-iron frame ──
	// Based on Harvia M3: glass door with sturdy iron frame
	const doorFrameW = WOOD_W * 0.58;
	const doorFrameH = combustionH * 0.52;
	const doorFrontZ = position.z + WOOD_D / 2;
	const doorCenterY = combustionCenterY + combustionH * 0.04; // slightly above center

	// Outer door frame (cast iron surround)
	const frameThick = 0.018;
	for (const side of [-1, 1]) {
		addBox(
			frameThick,
			doorFrameH,
			0.014,
			matHeaterDoor,
			position.x + (side * (doorFrameW - frameThick)) / 2,
			doorCenterY,
			doorFrontZ + 0.007,
			group,
		).name = `detail/heater/${namePrefix}-door-frame-side-${side}`;
		addBox(
			doorFrameW - 2 * frameThick,
			frameThick,
			0.014,
			matHeaterDoor,
			position.x,
			doorCenterY + (side * (doorFrameH - frameThick)) / 2,
			doorFrontZ + 0.007,
			group,
		).name = `detail/heater/${namePrefix}-door-frame-rail-${side}`;
	}

	// Glass insert (fire-visible window, slightly inset)
	const glassW = doorFrameW - frameThick * 2;
	const glassH = doorFrameH - frameThick * 2;
	addBox(
		glassW,
		glassH,
		0.005,
		matFireGlass,
		position.x,
		doorCenterY,
		doorFrontZ + 0.008,
		group,
	).name = `detail/heater/${namePrefix}-door-glass`;

	// Door handle — horizontal bar (like Narvi Luosto cast iron handle)
	const handleW = doorFrameW * 0.38;
	addBox(
		handleW,
		0.014,
		0.018,
		matChimney,
		position.x,
		doorCenterY + doorFrameH * 0.38,
		doorFrontZ + 0.02,
		group,
	).name = `detail/heater/${namePrefix}-door-handle`;

	// Door hinge details (2 small knobs on left side)
	for (const hy of [-0.3, 0.3]) {
		addBox(
			0.012,
			0.02,
			0.012,
			matChimney,
			position.x - doorFrameW / 2 - 0.002,
			doorCenterY + doorFrameH * hy,
			doorFrontZ + 0.012,
			group,
		).name = `detail/heater/${namePrefix}-hinge-${hy > 0 ? "top" : "bot"}`;
	}

	// ── Ash drawer (asklucka) — smaller drawer below fire door ──
	const ashW = WOOD_W * 0.48;
	const ashH = combustionH * 0.14;
	const ashY = bodyBottomY + ashH / 2 + 0.008;
	addBox(
		ashW,
		ashH,
		0.008,
		matHeater,
		position.x,
		ashY,
		doorFrontZ + 0.004,
		group,
	).name = `detail/heater/${namePrefix}-ash-drawer`;

	// Ash drawer handle (small knob)
	addBox(
		ashW * 0.3,
		0.008,
		0.012,
		matChimney,
		position.x,
		ashY,
		doorFrontZ + 0.014,
		group,
	).name = `detail/heater/${namePrefix}-ash-handle`;

	// Thin separation line between ash drawer and fire door
	addBox(
		WOOD_W * 0.7,
		0.003,
		0.008,
		matChimney,
		position.x,
		bodyBottomY + ashH + 0.012,
		doorFrontZ + 0.004,
		group,
	).name = `detail/heater/${namePrefix}-ash-divider`;

	const stones = new THREE.Mesh(
		createHeaterStones(WOOD_W - 0.026, stoneH - 0.012, WOOD_D - 0.026),
		matHeaterStone,
	);
	stones.position.set(position.x, stoneCenterY, position.z);
	stones.name = `detail/heater/${namePrefix}-stones`;
	stones.castShadow = true;
	stones.receiveShadow = true;
	group.add(stones);

	// ── Stainless steel cage bars (spjälverk) around stone basket ──
	// Vertical bars (like Harvia M3's upper grill)
	const barR = 0.003;
	const barH = stoneH;
	const numBarsPerSide = 5;

	// Front and back vertical bars
	for (let i = 0; i < numBarsPerSide; i++) {
		const t = i / (numBarsPerSide - 1) - 0.5;
		const bx = position.x + t * (WOOD_W - 0.04);

		// Front bars
		const frontBar = new THREE.Mesh(
			new THREE.CylinderGeometry(barR, barR, barH, 6),
			matStainlessCage,
		);
		frontBar.position.set(bx, stoneCenterY, position.z + WOOD_D / 2 - 0.002);
		frontBar.name = `detail/heater/${namePrefix}-bar-f${i}`;
		group.add(frontBar);

		// Back bars
		const backBar = new THREE.Mesh(
			new THREE.CylinderGeometry(barR, barR, barH, 6),
			matStainlessCage,
		);
		backBar.position.set(bx, stoneCenterY, position.z - WOOD_D / 2 + 0.002);
		backBar.name = `detail/heater/${namePrefix}-bar-b${i}`;
		group.add(backBar);
	}

	// Side vertical bars (fewer)
	for (let i = 0; i < 4; i++) {
		const t = i / 3 - 0.5;
		const bz = position.z + t * (WOOD_D - 0.04);

		const leftBar = new THREE.Mesh(
			new THREE.CylinderGeometry(barR, barR, barH, 6),
			matStainlessCage,
		);
		leftBar.position.set(position.x - WOOD_W / 2 + 0.002, stoneCenterY, bz);
		leftBar.name = `detail/heater/${namePrefix}-bar-l${i}`;
		group.add(leftBar);

		const rightBar = new THREE.Mesh(
			new THREE.CylinderGeometry(barR, barR, barH, 6),
			matStainlessCage,
		);
		rightBar.position.set(position.x + WOOD_W / 2 - 0.002, stoneCenterY, bz);
		rightBar.name = `detail/heater/${namePrefix}-bar-r${i}`;
		group.add(rightBar);
	}

	// Horizontal ring bars at top and mid-height of stone cage
	for (const ringY of [
		stoneCenterY - stoneH * 0.4,
		stoneCenterY + stoneH * 0.4,
	]) {
		// Front/back horizontal bars
		addBox(
			WOOD_W,
			barR * 2,
			barR * 2,
			matStainlessCage,
			position.x,
			ringY,
			position.z + WOOD_D / 2 - 0.002,
			group,
		).name = `detail/heater/${namePrefix}-ring-f-${ringY.toFixed(2)}`;
		addBox(
			WOOD_W,
			barR * 2,
			barR * 2,
			matStainlessCage,
			position.x,
			ringY,
			position.z - WOOD_D / 2 + 0.002,
			group,
		).name = `detail/heater/${namePrefix}-ring-b-${ringY.toFixed(2)}`;
		// Side horizontal bars
		addBox(
			barR * 2,
			barR * 2,
			WOOD_D,
			matStainlessCage,
			position.x - WOOD_W / 2 + 0.002,
			ringY,
			position.z,
			group,
		).name = `detail/heater/${namePrefix}-ring-l-${ringY.toFixed(2)}`;
		addBox(
			barR * 2,
			barR * 2,
			WOOD_D,
			matStainlessCage,
			position.x + WOOD_W / 2 - 0.002,
			ringY,
			position.z,
			group,
		).name = `detail/heater/${namePrefix}-ring-r-${ringY.toFixed(2)}`;
	}

	// ── 4 adjustable legs (cylindrical feet with base pads) ──
	const legR = 0.016;
	const legInset = 0.055;
	for (const [dx, dz] of [
		[-1, -1],
		[-1, 1],
		[1, -1],
		[1, 1],
	]) {
		const legX = position.x + dx * (WOOD_W / 2 - legInset);
		const legZ = position.z + dz * (WOOD_D / 2 - legInset);
		const legBottomY = position.y - WOOD_H / 2;

		// Main leg cylinder
		const leg = new THREE.Mesh(
			new THREE.CylinderGeometry(legR, legR, WOOD_LEG_H, 8),
			matHeater,
		);
		leg.position.set(legX, legBottomY + WOOD_LEG_H / 2, legZ);
		leg.castShadow = true;
		leg.receiveShadow = true;
		leg.name = `detail/heater/${namePrefix}-leg-${dx > 0 ? "r" : "l"}${dz > 0 ? "f" : "b"}`;
		group.add(leg);

		// Small foot pad
		const pad = new THREE.Mesh(
			new THREE.CylinderGeometry(legR + 0.006, legR + 0.008, 0.006, 8),
			matChimney,
		);
		pad.position.set(legX, legBottomY + 0.003, legZ);
		pad.name = `detail/heater/${namePrefix}-pad-${dx > 0 ? "r" : "l"}${dz > 0 ? "f" : "b"}`;
		group.add(pad);
	}

	// ── Fire glow light — directional spill from the door opening only ──
	const fireLight = new THREE.SpotLight(0xff6d2f, 3.1, 1.65, 0.48, 0.72, 1.8);
	fireLight.position.set(position.x, doorCenterY - 0.02, doorFrontZ + 0.09);
	fireLight.castShadow = false;
	fireLight.name = `detail/heater/${namePrefix}-fire-glow`;
	const fireTarget = new THREE.Object3D();
	fireTarget.position.set(position.x, ashY + 0.02, doorFrontZ + 0.62);
	fireTarget.name = `detail/heater/${namePrefix}-fire-target`;
	group.add(fireTarget);
	fireLight.target = fireTarget;
	group.add(fireLight);

	// Soft omnidirectional ember pool — fills the interior with warm
	// bounce that the directional spotlight intentionally does not.
	// Replaces the pre-`10c7ebe` PointLight(0xff6622, 1.8, …) that gave
	// the "warm everywhere" feel before the spot refactor focused the
	// beam out the door. Cheap (no shadows). Gated by
	// heaterAnimationEnabled at reset time so it stays off on `low`
	// (mobile budget).
	const emberLight = new THREE.PointLight(0xff7a3a, 0.85, 2.4, 2.0);
	emberLight.position.set(position.x, stoneCenterY + stoneH * 0.35, position.z);
	emberLight.castShadow = false;
	emberLight.userData.basePosition = emberLight.position.clone();
	emberLight.name = `detail/heater/${namePrefix}-ember-glow`;
	group.add(emberLight);

	for (const [index, xOffset] of [-0.055, 0.065].entries()) {
		const steamMaterial = new THREE.SpriteMaterial({
			map: STEAM_TEXTURE,
			color: 0xf7efe1,
			transparent: true,
			opacity: 0.025,
			depthWrite: false,
		});
		const steam = new THREE.Sprite(steamMaterial);
		steam.position.set(
			position.x + xOffset,
			stoneCenterY + stoneH * 0.62,
			position.z + (index === 0 ? -0.03 : 0.028),
		);
		steam.scale.set(0.22, 0.3, 1);
		steam.name = `detail/heater/${namePrefix}-steam-${index + 1}`;
		steam.userData.baseY = steam.position.y;
		steam.userData.baseX = steam.position.x;
		steam.userData.baseZ = steam.position.z;
		steam.userData.phase = index * 1.7;
		group.add(steam);
	}

	for (const child of [...group.children]) {
		if (WOOD_PROCEDURAL_PART.test(child.name)) parent.add(child);
	}
}

// ─── Electric heater ────────────────────────────────────────

function buildElectricHeater(
	position: THREE.Vector3,
	group: THREE.Group,
	namePrefix: string,
	premiumAsset = false,
) {
	// Everything but the stones lives in a shell the viewer can swap for the
	// modelled tower (scripts/blender/electric-heater.py); the stones stay
	// procedural and show through the asset's open basket.
	const shell = new THREE.Group();
	shell.name = `detail/heater/${namePrefix}-shell`;
	shell.position.copy(position);
	group.add(shell);
	if (premiumAsset) {
		markPremiumSlot(shell, {
			assetPath: ELECTRIC_HEATER_ASSET_PATH,
			origin: [0, -ELEC_H / 2, 0],
		});
	}
	const o = new THREE.Vector3(0, 0, 0);

	// Main tower body — lower section only. The upper ~40% is the open stone
	// cage; a full-height body would bury the stones completely inside it.
	const bodyH = ELEC_H - ELEC_H * 0.4 - 0.02;
	addBox(
		ELEC_W,
		bodyH,
		ELEC_D,
		matHeaterElectric,
		o.x,
		o.y - ELEC_H / 2 + bodyH / 2,
		o.z,
		shell,
	).name = `detail/heater/${namePrefix}-body`;

	// Vertical fin accents on left and right sides — kept within the solid
	// body section so they don't float beside the open stone cage above it.
	const finH = bodyH * 0.8;
	const finW = 0.008;
	const finD = ELEC_D * 0.7;
	const finY = o.y - ELEC_H / 2 + bodyH / 2;
	const finCount = 3;
	for (let i = 0; i < finCount; i++) {
		const fz = o.z - finD * 0.4 + (finD * 0.8 * i) / (finCount - 1);
		// Left side fins
		addBox(
			finW,
			finH,
			0.008,
			matChimney,
			o.x - ELEC_W / 2 - finW / 2,
			finY,
			fz,
			shell,
		).name = `detail/heater/${namePrefix}-fin-l${i + 1}`;
		// Right side fins
		addBox(
			finW,
			finH,
			0.008,
			matChimney,
			o.x + ELEC_W / 2 + finW / 2,
			finY,
			fz,
			shell,
		).name = `detail/heater/${namePrefix}-fin-r${i + 1}`;
	}

	// Stone cage — upper portion of the tower (open & visible stones)
	const stoneH = ELEC_H * 0.4;
	const stoneY = position.y + ELEC_H / 2 - stoneH / 2 - 0.02;
	const stones = new THREE.Mesh(
		createHeaterStones(ELEC_W - 0.026, stoneH, ELEC_D - 0.026),
		matHeaterStone,
	);
	stones.position.set(position.x, stoneY, position.z);
	stones.name = `detail/heater/${namePrefix}-stones`;
	stones.castShadow = true;
	stones.receiveShadow = true;
	group.add(stones);

	// Wire cage vertical posts around stones
	const cagePost = 0.01;
	const cageW = ELEC_W + 0.01;
	const cageD = ELEC_D + 0.01;
	for (const [dx, dz] of [
		[-1, -1],
		[-1, 1],
		[1, -1],
		[1, 1],
	]) {
		addBox(
			cagePost,
			stoneH + 0.01,
			cagePost,
			matChimney,
			o.x + dx * (cageW / 2),
			stoneY,
			o.z + dz * (cageD / 2),
			shell,
		).name =
			`detail/heater/${namePrefix}-cage-${dx > 0 ? "r" : "l"}${dz > 0 ? "f" : "b"}`;
	}

	// Horizontal cage rings (3 levels)
	for (const fy of [-0.3, 0, 0.3]) {
		const ringY = stoneY + fy * stoneH;
		addBox(
			cageW,
			cagePost,
			cagePost,
			matChimney,
			o.x,
			ringY,
			o.z - cageD / 2,
			shell,
		).name = `detail/heater/${namePrefix}-ring-b-${fy}`;
		addBox(
			cageW,
			cagePost,
			cagePost,
			matChimney,
			o.x,
			ringY,
			o.z + cageD / 2,
			shell,
		).name = `detail/heater/${namePrefix}-ring-f-${fy}`;
		addBox(
			cagePost,
			cagePost,
			cageD,
			matChimney,
			o.x - cageW / 2,
			ringY,
			o.z,
			shell,
		).name = `detail/heater/${namePrefix}-ring-l-${fy}`;
		addBox(
			cagePost,
			cagePost,
			cageD,
			matChimney,
			o.x + cageW / 2,
			ringY,
			o.z,
			shell,
		).name = `detail/heater/${namePrefix}-ring-r-${fy}`;
	}

	// Control panel indicator — small accent on lower front
	const panelW = ELEC_W * 0.35;
	const panelH = 0.025;
	addBox(
		panelW,
		panelH,
		0.006,
		matHeaterDoor,
		o.x,
		o.y - ELEC_H / 2 + ELEC_H * 0.12,
		o.z + ELEC_D / 2 + 0.003,
		shell,
	).name = `detail/heater/${namePrefix}-panel`;
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Build a fully detailed procedural heater and add it to the given group.
 * The position should be the center of the heater volume.
 * @param rotationY  Optional Y-axis rotation in radians (default 0).
 *                   Use Math.PI to flip the door 180° (e.g. in two-room saunas).
 */
export function buildHeaterGroup(
	heater: HeaterRef,
	position: THREE.Vector3,
	group: THREE.Group,
	namePrefix: string,
	rotationY = 0,
): void {
	const heaterType = heaterKindOf(heater);
	const model =
		typeof heater === "string" ? undefined : resolveHeaterModel(heater);
	if (model) {
		buildCatalogHeater(model, position, group, namePrefix, rotationY);
		return;
	}
	if (rotationY === 0) {
		// Fast path — no rotation needed, build directly
		if (heaterType === "wood") {
			buildWoodHeater(position, group, namePrefix, true);
		} else {
			buildElectricHeater(position, group, namePrefix, true);
		}
	} else {
		// Build at origin in a temporary group, rotate, then translate into place
		const pivot = new THREE.Group();
		pivot.name = `detail/heater/${namePrefix}-pivot`;
		const origin = new THREE.Vector3(0, position.y, 0);
		if (heaterType === "wood") {
			buildWoodHeater(origin, pivot, namePrefix, true);
		} else {
			buildElectricHeater(origin, pivot, namePrefix, true);
		}
		pivot.rotation.y = rotationY;
		pivot.position.set(position.x, 0, position.z);
		group.add(pivot);
	}
}

/** Room-side parts an outside-fired unit does not have: it is fed from the
 *  far end of its tunnel, so the door, ash drawer and door light move there. */
const ROOM_SIDE_FIRING_PART = /-(door-|hinge-|ash-|fire-glow|fire-target)/;

/**
 * A catalog model (issue 159): the type's procedural unit, stretched to the
 * datasheet box so the 3D stands exactly where placement and clearance
 * measured it. An outside-fired model also gets its feed tunnel, reaching
 * back through the wall it stands against, with the fire door at its end.
 */
function buildCatalogHeater(
	model: HeaterModel,
	position: THREE.Vector3,
	group: THREE.Group,
	namePrefix: string,
	rotationY: number,
): void {
	const isWood = model.heaterType === "wood";
	const w = model.bodyMm.width / 1000;
	const h = model.bodyMm.height / 1000;
	const d = model.bodyMm.depth / 1000;

	const pivot = new THREE.Group();
	pivot.name = `detail/heater/${namePrefix}-pivot`;
	const body = new THREE.Group();
	body.name = `detail/heater/${namePrefix}-model-${model.id}`;
	const origin = new THREE.Vector3(0, 0, 0);
	if (isWood) {
		buildWoodHeater(origin, body, namePrefix);
		body.scale.set(w / WOOD_W, h / WOOD_H, d / WOOD_D);
	} else {
		buildElectricHeater(origin, body, namePrefix);
		body.scale.set(w / ELEC_W, h / ELEC_H, d / ELEC_D);
	}
	pivot.add(body);

	if (model.firing === "outside" && model.throughWallMm) {
		// Door parts sit inside the heater's shell group, the glass beside it.
		const roomSide: THREE.Object3D[] = [];
		body.traverse((child) => {
			if (ROOM_SIDE_FIRING_PART.test(child.name)) roomSide.push(child);
		});
		for (const child of roomSide) child.removeFromParent();

		// Local −Z is the wall the unit backs onto (the door faced +Z).
		const tunnelD = model.throughWallMm / 1000;
		const tunnelW = w * 0.6;
		const tunnelH = h * 0.3;
		const tunnelY = -h / 2 + WOOD_LEG_H + tunnelH / 2 + 0.05;
		const tunnelZ = -(d / 2 + tunnelD / 2);
		addBox(
			tunnelW,
			tunnelH,
			tunnelD,
			matHeater,
			0,
			tunnelY,
			tunnelZ,
			pivot,
		).name = `detail/heater/${namePrefix}-feed-tunnel`;
		addBox(
			tunnelW * 0.8,
			tunnelH * 0.8,
			0.008,
			matHeaterDoor,
			0,
			tunnelY,
			-(d / 2 + tunnelD) - 0.004,
			pivot,
		).name = `detail/heater/${namePrefix}-feed-door`;
	}

	pivot.rotation.y = rotationY;
	pivot.position.copy(position);
	group.add(pivot);
}

/** Overall width of the heater (for placement calculations). */
export function getHeaterWidth(ref: HeaterRef): number {
	return getHeaterWidthM(ref);
}

/** Overall depth of the heater's in-room body (for placement calculations). */
export function getHeaterDepth(ref: HeaterRef): number {
	return getHeaterDepthM(ref);
}

/** Overall height of the heater (for placement calculations). */
export function getHeaterHeight(ref: HeaterRef): number {
	return getHeaterHeightM(ref);
}

/**
 * The unit's plan-view footprint in the interior model's units and frame —
 * millimetres, NOT oriented against any wall. An authored layout may stand the
 * aggregat on a short wall, and `heaterExtentsFor` is what swaps the pair at
 * that point; storing an already-oriented pair is the bug `HeaterSizeMm`
 * documents.
 */
export function getHeaterSizeMm(ref: HeaterRef): HeaterSizeMm {
	return {
		widthMm: getHeaterWidth(ref) * 1000,
		depthMm: getHeaterDepth(ref) * 1000,
	};
}
